import { Box, Heading, Stack, Text } from '@chakra-ui/react'
import type { ReactElement } from 'react'

import { pluralize } from '@/utils/helpers'

import SeriesAssessmentQ from '@/queries/SeriesAssessmentQ'
import SeriesQ from '@/queries/SeriesQ'
import SeriesTemplateQ from '@/queries/SeriesTemplateQ'
import UserQ from '@/queries/UserQ'
import { useAppContext } from '@/client/context/app'
import { useUser } from '@/client/context/user'
import type { Query } from '@/client/query'
import { SAT_BOOTCAMP_SERIES_TEMPLATE_SLUGS } from '@/client/services/satBootcamp'
import { getShareDescriptions } from '@/client/services/share'
import * as url from '@/client/services/url'
import { getSeriesUrl } from '@/client/services/url'
import hasUserAuthScope from '@/client/services/user/hasUserAuthScope'

import MoreOptionsButton from '@/components/MoreOptionsButton'
import Tabs from '@/components/Tabs/Tabs'
import Container from '@/components/Worlds/UI/Container/Container'

import { SeriesTab } from '../types'
import Dot from './internal/Dot'
import EditSeriesButton from './internal/EditSeriesButton'
import RegisteredButton from './internal/RegisteredButton'
import SATScoreBand from './internal/SATScoreBand'
import SeriesIcon from './internal/SeriesIcon'
import SeriesLearnerTally from './internal/SeriesLearnerTally'

export default function SeriesNavbar({
  series,
  currentTab,
}: {
  series: Query
  currentTab: SeriesTab
}): ReactElement {
  const user = useUser()
  const { dryRun } = useAppContext()

  const seriesId = series(SeriesQ.id) ?? 0
  const seriesTitle = series(SeriesQ.title) ?? ''
  const seriesSessionCount = series(SeriesQ.countSessions()) ?? 0
  const satScoreBand = series(SeriesQ.getScoreBand())
  const seriesTemplate = series.one(SeriesQ.getSeriesTemplate())
  const seriesTemplateSlug = seriesTemplate?.(SeriesTemplateQ.slug) ?? null
  const isSATSeries =
    seriesTemplateSlug != null && SAT_BOOTCAMP_SERIES_TEMPLATE_SLUGS.includes(seriesTemplateSlug)
  const isUserHost = series(SeriesQ.isHost({ userId: user.id })) ?? false
  const isUserRegistered = series(SeriesQ.isUserRegistered(user.id)) ?? false
  const tutor = series.one(SeriesQ.getUser())
  const tutorId = tutor(UserQ.id) ?? 0
  const isParticipant = isUserRegistered || isUserHost

  const seriesAssessments = series
    .many(SeriesAssessmentQ.where({ seriesId: SeriesQ.id }))
    .map(seriesAssessment => seriesAssessment(SeriesAssessmentQ.id))

  const { long: shareDescriptionLong, short: shareDescriptionShort } = getShareDescriptions({
    type: 'session',
    title: seriesTitle,
    isTutor: tutorId === user.id,
  })

  const canEditSeries = isUserHost || hasUserAuthScope(user, 'admin:series_or_session:update')
  const actionButtons = (
    <>
      {canEditSeries || dryRun ? (
        <EditSeriesButton series={series} isSATSeries={isSATSeries} isUserHost={isUserHost} />
      ) : null}
      {(isParticipant && !isUserHost) || dryRun ? <RegisteredButton series={series} /> : null}
      <MoreOptionsButton
        type="series"
        typeId={seriesId}
        tutorId={tutorId}
        shareOptions={{
          url: url.getSeriesUrlById(seriesId),
          description: shareDescriptionLong,
          shortDescription: shareDescriptionShort,
        }}
        sessionOrSeries={series}
        iconColor="fg.subtle"
      />
    </>
  )

  const seriesTabToUrlRecord: Record<SeriesTab, string> = {
    Learn: getSeriesUrl(seriesId),
    Assessments: getSeriesUrl(seriesId, { path: 'assessments' }),
    'Group Chat': getSeriesUrl(seriesId, { path: 'messages' }),
    Participants: getSeriesUrl(seriesId, { path: 'members' }),
  }
  const seriesTabToUrl = (t: SeriesTab) => seriesTabToUrlRecord[t]
  const seriesTabs: SeriesTab[] = [SeriesTab.LEARN]

  if (
    (isParticipant && seriesAssessments.length > 0) ||
    isUserHost ||
    hasUserAuthScope(user, 'admin:assessments:view')
  ) {
    seriesTabs.push(SeriesTab.ASSESSMENTS)
  }
  if (isParticipant || hasUserAuthScope(user, 'admin:messages:group:read')) {
    seriesTabs.push(SeriesTab.GROUP_CHAT)
  }
  if (isParticipant || hasUserAuthScope(user, 'admin:series:members:view')) {
    seriesTabs.push(SeriesTab.PARTICIPANTS)
  }

  const currentSeriesTab = currentTab == null ? seriesTabs[0] : currentTab

  return (
    <Container pt={{ base: '16px', md: '32px' }} px={{ base: '16px', md: '32px' }}>
      <Stack
        spacing="16px"
        borderBottom="2px solid"
        borderColor="border"
        pb={seriesTabs.length > 1 ? 0 : { base: '16px', md: '32px' }}
      >
        <Stack
          direction={{ base: 'column', lg: 'row' }}
          spacing={{ base: '24px', md: '32px' }}
          justifyContent="space-between"
          align="flex-start"
        >
          <Stack
            direction="row"
            align="center"
            justify="space-between"
            display={{ base: 'flex', md: 'none' }}
            width={{ base: '100%', md: 'unset' }}
          >
            {actionButtons}
          </Stack>
          <Stack direction="row" spacing="24px">
            <SeriesIcon series={series} />
            <Stack spacing={{ base: '16px', md: '0px' }} direction="column" justify="center">
              <Heading size="2xl" as="span">
                {seriesTitle}
              </Heading>
              <Stack
                spacing={{ base: '12px', md: '6px' }}
                direction={{ base: 'column', md: 'row' }}
                alignItems={{ base: 'flex-start', md: 'center' }}
              >
                <Stack
                  display={satScoreBand != null ? 'flex' : 'none'}
                  spacing="8px"
                  direction="row"
                  alignItems="center"
                >
                  <SATScoreBand series={series} />
                  <Dot display={{ base: 'none', md: 'block' }} />
                </Stack>
                <Text variant="eyebrow-small" color="fg.muted">
                  {pluralize(seriesSessionCount, 'session')}
                </Text>
                <SeriesLearnerTally series={series} />
              </Stack>
            </Stack>
          </Stack>
          <Stack direction="row" align="center" display={{ base: 'none', md: 'flex' }}>
            {actionButtons}
          </Stack>
        </Stack>

        {seriesTabs.length > 1 ? (
          <Box mb="-2px">
            <Tabs<SeriesTab>
              currentTab={currentSeriesTab}
              tabs={seriesTabs}
              tabToUrl={seriesTabToUrl}
              hideBorder
            />
          </Box>
        ) : null}
      </Stack>
    </Container>
  )
}
